import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TextField, Grid, IconButton, InputAdornment } from '@mui/material';
import { useAPIAuthenticatedService } from '../helpers/APICallsAuthenticated';
import { CacheKeys, OfferCreationAttributes_FromUser } from '../helpers/DataStructures';
import AsyncActionButton, { AsyncActionButtonAbortError } from '../components/AsyncActionButton';


import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

interface CreateOfferFormProps {
  extraButton?: JSX.Element;
  mode: "Edit" | "Create"; 
}

const parseOfferCashAmount = (value: unknown) => {
  const parsedValue = typeof value === 'string' ? Number(value.trim()) : Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

// REQ-040: authenticated offer reward controls use an escaped pound sign to avoid mojibake.
const OFFER_POUND_SIGN = '\u00A3';

const CreateOfferForm: React.FC<CreateOfferFormProps> = ({extraButton, mode}) => {

  const apiServiceInstance = useAPIAuthenticatedService();

  const { data: data_UsersOffer } = useQuery(
    [CacheKeys.LOGGED_IN_USERS_OFFER], 
    async () => {
      const user = await apiServiceInstance.getOffersForAuthenticatedUser();
      return user;
    }
  );

  const { handleSubmit, control, formState: { errors }, getValues, setError, setValue, reset } = useForm<OfferCreationAttributes_FromUser>({
    defaultValues: {
      theOffer: data_UsersOffer ? data_UsersOffer.theOffer : "",
      location: data_UsersOffer ? data_UsersOffer.location : "",
      offeringFixedCashAmount: data_UsersOffer ? data_UsersOffer.offeringFixedCashAmount : 50,
    }
  });

  useEffect(() => {
    if (mode !== 'Edit' || !data_UsersOffer) {
      return;
    }

    reset({
      theOffer: data_UsersOffer.theOffer ?? '',
      location: data_UsersOffer.location ?? '',
      offeringFixedCashAmount: data_UsersOffer.offeringFixedCashAmount ?? 50,
    });
  }, [data_UsersOffer, mode, reset]);

  const queryClient = useQueryClient();

  const { mutateAsync } =
    useMutation([CacheKeys.LOGGED_IN_USERS_OFFER], apiServiceInstance.createOffer,
    {
        onSuccess: () => {
            queryClient.invalidateQueries([CacheKeys.LOGGED_IN_USERS_OFFER]);
        }
    },
  );

  const submitOffer = handleSubmit(
    async (data: OfferCreationAttributes_FromUser) => {
      const parsedCashAmount = parseOfferCashAmount(data.offeringFixedCashAmount);

      if (parsedCashAmount === null) {
        setError('offeringFixedCashAmount', {
          type: 'validate',
          message: 'Enter a valid cash amount',
        });
        throw new AsyncActionButtonAbortError();
      }

      if (parsedCashAmount > 995) {
        setError('offeringFixedCashAmount', {
          type: 'max',
          message: `Maximum amount is ${OFFER_POUND_SIGN}995`,
        });
        throw new AsyncActionButtonAbortError();
      }

      await mutateAsync({
        ...data,
        // REQ-031: authenticated offer saves must honor the backend integer contract.
        offeringFixedCashAmount: parsedCashAmount,
      });
    },
    async () => {
      const parsedCashAmount = parseOfferCashAmount(getValues('offeringFixedCashAmount'));

      if (parsedCashAmount === null) {
        setError('offeringFixedCashAmount', {
          type: 'validate',
          message: 'Enter a valid cash amount',
        });
      } else if (parsedCashAmount > 995) {
        setError('offeringFixedCashAmount', {
          type: 'max',
          message: `Maximum amount is ${OFFER_POUND_SIGN}995`,
        });
      }

      throw new AsyncActionButtonAbortError();
    }
  );

  const successLabel = mode === 'Create' ? 'Offer Created' : 'Offer Saved';
  const successToast = mode === 'Create' ? 'Offer created' : 'Offer saved';
  const pendingLabel = mode === 'Create' ? 'Creating...' : 'Saving...';

  const adjustCashAmount = (delta: number) => {
    const currentValue = getValues("offeringFixedCashAmount");
    const parsedValue = parseOfferCashAmount(currentValue);

    if (parsedValue === null) {
      // REQ-031: stepper controls must not coerce invalid typed amounts to zero.
      setError('offeringFixedCashAmount', {
        type: 'validate',
        message: 'Enter a valid cash amount',
      });
      return;
    }

    const newValue = Math.min(Math.max(parsedValue + delta, 0), 995);
    setValue("offeringFixedCashAmount", newValue, { shouldValidate: true });
  };

  return (
    <form>
      <Grid container direction="column" spacing={2}>
        <Grid item xs={12}>
            <Controller
                name="offeringFixedCashAmount"
                control={control}
                rules={{ 
                required: 'Cash amount is required',
                // REQ-031/REQ-040: reject typed invalid reward values with stable currency text.
                validate: (value) => {
                  const parsedValue = parseOfferCashAmount(value);
                  if (parsedValue === null) {
                    return 'Enter a valid cash amount';
                  }

                  return parsedValue <= 995 || `Maximum amount is ${OFFER_POUND_SIGN}995`;
                },
                min: {value: 0, message: "Can't offer less than zero"},
                max: {value: 995, message: `Maximum amount is ${OFFER_POUND_SIGN}995`}
                }}
                render={({ field }) => {
                  const parsedStepperValue = parseOfferCashAmount(field.value);

                  return (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <TextField 
                    {...field} 
                    label="How much will you offer?" 
                    error={Boolean(errors.offeringFixedCashAmount)} 
                    helperText={errors.offeringFixedCashAmount?.message} 
                    fullWidth 
                    InputProps={{
                        startAdornment: <InputAdornment position="start">{OFFER_POUND_SIGN}</InputAdornment>,
                    }}
                    />
                    <IconButton 
                        aria-label="Increase cash amount"
                        size="small"
                        onClick={() => adjustCashAmount(5)}
                        disabled={parsedStepperValue !== null && parsedStepperValue >= 995}
                    >
                        <AddIcon />
                    </IconButton>
                    <IconButton 
                    aria-label="Decrease cash amount"
                    size="small"
                    onClick={() => adjustCashAmount(-5)}
                    disabled={parsedStepperValue !== null && parsedStepperValue <= 0}
                    >
                        <RemoveIcon />
                    </IconButton>
                </div>
                  );
                }}
            />
        </Grid>

        <Grid item xs={12}>
            <Controller
            name="location"
                control={control}
                rules={{ required: 'Area is required' }}
                render={({ field }) => <TextField {...field} label="What area do you cover?" error={Boolean(errors.location)} 
                
                //helperText={errors.location?.message} 
                helperText={errors.location?.message} 
                
                fullWidth />}
            />
        </Grid>

            <Grid item xs={12}>
            <Controller
                name="theOffer"
                control={control}
                rules={{ maxLength: 1000 }}
                render={({ field }) => 
                <TextField multiline rows={3} {...field} 
                  label="Services you provide" 
                  error={Boolean(errors.theOffer)} 
                  helperText={errors.theOffer?.message || (errors.theOffer?.type === "maxLength" && "text exceeded maximum length")}      
                  fullWidth />}
            />
        </Grid>

        <Grid item xs={12}>
          <AsyncActionButton
            errorToast="Error"
            idleLabel="Save"
            onAction={() => submitOffer()}
            pendingLabel={pendingLabel}
            styleVariant="green"
            successDisplayMs={200}
            successLabel={successLabel}
            successToast={successToast}
          />{' '}
          {extraButton}
        </Grid>
        
      </Grid>
    </form>
  );
};

export default CreateOfferForm;
